import { useState, useEffect } from 'react'
import { X, Loader2, AlertCircle, CheckCircle, ExternalLink } from 'lucide-react'
import { createMCPConnection, testMCPConnection, deleteMCPConnection, startOAuthConnect, type MCPServer, type MCPConnectionCreate, type MCPConnection } from '../services/mcp'

interface ConnectionModalProps {
  isOpen: boolean
  onClose: () => void
  server: MCPServer | null
  connection?: MCPConnection | null
  onSuccess: () => void
}

// Words that should always be fully uppercase in labels
const ACRONYMS = new Set(['url', 'api', 'id', 'ui', 'db', 'sql', 'aws', 'iam', 'ssh', 'tls', 'ssl', 'jwt', 'sap', 'pat', 'oauth', 'crm', 'erp', 'sdk'])

function formatFieldKey(key: string): string {
  return key
    .split('_')
    .map(word => ACRONYMS.has(word.toLowerCase())
      ? word.toUpperCase()
      : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(' ')
}

function getFieldLabel(key: string, fieldSchema?: any): string {
  return fieldSchema?.title || fieldSchema?.label || formatFieldKey(key)
}

export function ConnectionModal({ isOpen, onClose, server, connection, onSuccess }: ConnectionModalProps) {
  const [connectionName, setConnectionName] = useState('')
  const [config, setConfig] = useState<Record<string, any>>({})
  const [isSharedWithTeam, setIsSharedWithTeam] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isTesting, setIsTesting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string; details?: any } | null>(null)
  const [createdConnectionId, setCreatedConnectionId] = useState<string | null>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  // Token entry is an explicit escape hatch when OAuth is available —
  // self-hosted instances (GitHub Enterprise, Jira Server/DC) and
  // service-account tokens are the legitimate reasons to reach for it.
  const [showManualForm, setShowManualForm] = useState(false)
  const isEditMode = Boolean(connection)
  // OAuth is only offered for NEW connections — editing an OAuth connection
  // means reconnecting (tokens can't be meaningfully hand-edited).
  const offersOAuth = Boolean(server?.oauth_enabled) && !isEditMode
  const manualVisible = !offersOAuth || showManualForm

  // Pre-populate form when editing
  useEffect(() => {
    if (connection && isOpen) {
      setConnectionName(connection.connection_name)
      setIsSharedWithTeam(connection.is_shared_with_team)
      // Note: We cannot decrypt the config, so we leave it empty
      // User will need to re-enter credentials when editing
    }
  }, [connection, isOpen])

  if (!isOpen || !server) return null

  const handleConfigChange = (key: string, value: any) => {
    setConfig(prev => ({ ...prev, [key]: value }))
    setTestResult(null) // Clear test result when config changes
  }

  const handleOAuthConnect = async () => {
    setIsRedirecting(true)
    setError(null)
    try {
      const authorizationUrl = await startOAuthConnect(
        server.server_key,
        `${window.location.origin}/app/integrations`,
        {
          connectionName: connectionName.trim() || undefined,
          isSharedWithTeam,
        }
      )
      // Full-page redirect to the provider's consent screen. The gateway
      // sends the browser back to /app/integrations with the result.
      window.location.href = authorizationUrl
    } catch (err: any) {
      setError(err.message || `Could not start ${server.name} sign-in`)
      setIsRedirecting(false)
    }
  }

  const handleTest = async () => {
    // Validate required fields first (standard form validation)
    if (!connectionName.trim()) {
      setError('Connection name is required')
      return
    }

    const requiredFields = server.config_schema?.required || []
    for (const field of requiredFields) {
      if (!config[field]) {
        setError(`${getFieldLabel(field, server.config_schema?.properties?.[field])} is required`)
        return
      }
    }

    setIsTesting(true)
    setError(null)
    setTestResult(null)

    try {
      // Create temporary connection to test
      const newConnection: MCPConnectionCreate = {
        server_key: server.server_key,
        connection_name: connectionName,
        config,
        is_shared_with_team: isSharedWithTeam,
      }

      const createdConnection = await createMCPConnection(newConnection)
      setCreatedConnectionId(createdConnection.id) // Track the created connection

      // Test the connection
      try {
        const testResult = await testMCPConnection(createdConnection.id)

        if (testResult.success) {
          setTestResult({
            success: true,
            message: testResult.message || 'Connection test successful!',
            details: testResult.details
          })
        } else {
          // Delete the failed connection — it shouldn't appear in My Connections
          try { await deleteMCPConnection(createdConnection.id) } catch {}
          setCreatedConnectionId(null)
          setTestResult({
            success: false,
            message: testResult.message || 'Connection test failed',
            details: testResult.details
          })
        }
      } catch (testErr: any) {
        // Delete the failed connection — it shouldn't appear in My Connections
        try { await deleteMCPConnection(createdConnection.id) } catch {}
        setCreatedConnectionId(null)
        setTestResult({
          success: false,
          message: testErr.message || 'Connection test failed'
        })
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create connection')
    } finally {
      setIsTesting(false)
    }
  }

  const handleSave = async () => {
    // If test passed and connection already created, just close the modal
    if (testResult?.success && createdConnectionId) {
      onSuccess()
      handleClose()
      return
    }

    // Validate required fields
    if (!connectionName.trim()) {
      setError('Connection name is required')
      return
    }

    // Only validate required fields for new connections
    // For edits, only validate if user entered a new value
    if (!isEditMode) {
      const requiredFields = server.config_schema?.required || []
      for (const field of requiredFields) {
        if (!config[field]) {
          setError(`${getFieldLabel(field, server.config_schema?.properties?.[field])} is required`)
          return
        }
      }
    }

    setIsSaving(true)
    setError(null)

    try {
      // Only create if we haven't already created one via test
      if (!createdConnectionId) {
        await createMCPConnection({
          server_key: server.server_key,
          connection_name: connectionName,
          config,
          is_shared_with_team: isSharedWithTeam,
        })
      }

      onSuccess()
      handleClose()
    } catch (err: any) {
      setError(err.message || 'Failed to create connection')
    } finally {
      setIsSaving(false)
    }
  }

  const handleClose = () => {
    // If a connection was created but test wasn't successful, delete it
    if (createdConnectionId && !testResult?.success) {
      deleteMCPConnection(createdConnectionId).catch(() => {})
    }
    setConnectionName('')
    setConfig({})
    setIsSharedWithTeam(false)
    setError(null)
    setTestResult(null)
    setCreatedConnectionId(null)
    setShowManualForm(false)
    onClose()
  }

  const renderConfigField = (key: string, fieldSchema: any) => {
    const fieldType = fieldSchema.type || 'string'
    const description = fieldSchema.description || ''
    const format = fieldSchema.format || ''
    const isSensitive = format === 'password' || key.toLowerCase().includes('password') || key.toLowerCase().includes('secret') || key.toLowerCase().includes('token')

    // Check if field is required (from top-level schema.required array)
    const requiredFields = server.config_schema?.required || []
    const isRequired = requiredFields.includes(key) && !isEditMode // Only required for new connections

    const label = getFieldLabel(key, fieldSchema)

    // Different placeholder for edit vs create mode
    const placeholder = isEditMode && isSensitive
      ? '••••••••' // Indicate saved value exists
      : fieldSchema.example || fieldSchema.placeholder || `Enter ${label.toLowerCase()}`

    return (
      <div key={key} className="space-y-1">
        <label className="block text-sm font-medium text-foreground">
          {label}
          {isRequired && <span className="text-destructive ml-1">*</span>}
          {isEditMode && isSensitive && !isRequired && <span className="text-xs text-muted-foreground ml-2">(optional - leave blank to keep existing)</span>}
        </label>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
        {fieldType === 'boolean' ? (
          <input
            type="checkbox"
            checked={config[key] || false}
            onChange={(e) => handleConfigChange(key, e.target.checked)}
            className="h-4 w-4 text-success-foreground focus:ring-success border rounded"
          />
        ) : (
          <input
            type={isSensitive ? 'password' : format === 'email' ? 'email' : format === 'uri' ? 'url' : 'text'}
            value={config[key] || ''}
            onChange={(e) => handleConfigChange(key, e.target.value)}
            placeholder={placeholder}
            className="w-full px-3 py-2 border border-strong bg-card text-foreground placeholder:text-subtle-foreground rounded-lg focus:ring-2 focus:ring-success focus:border-success"
          />
        )}
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-strong">
          <div>
            <h2 className="text-2xl font-bold text-foreground">
              {isEditMode ? 'Edit Connection' : `Connect to ${server.name}`}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">{server.description}</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-foreground/[0.06] rounded-lg transition"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-4 bg-destructive-soft border border-destructive/30 rounded-lg">
              <AlertCircle className="w-5 h-5 text-destructive-foreground flex-shrink-0" />
              <p className="text-sm text-destructive-foreground">{error}</p>
            </div>
          )}

          {/* Test Result */}
          {testResult && (
            <div className={`p-4 rounded-lg border ${
              testResult.success
                ? 'bg-success-soft border-success/30'
                : 'bg-destructive-soft border-destructive/30'
            }`}>
              <div className="flex items-center gap-2 mb-3">
                {testResult.success ? (
                  <CheckCircle className="w-5 h-5 text-success-foreground flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-destructive-foreground flex-shrink-0" />
                )}
                <p className={`text-sm font-medium ${testResult.success ? 'text-success-foreground' : 'text-destructive-foreground'}`}>
                  {testResult.message}
                </p>
              </div>

              {/* Account Details */}
              {testResult.success && testResult.details && (
                <div className="mt-3 pt-3 border-t border-success/30 space-y-2">
                  {testResult.details.user && (
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-success-foreground">Account Information:</p>
                      {testResult.details.user.login && (
                        <p className="text-xs text-success-foreground">Username: <span className="font-medium">{testResult.details.user.login}</span></p>
                      )}
                      {testResult.details.user.name && (
                        <p className="text-xs text-success-foreground">Name: <span className="font-medium">{testResult.details.user.name}</span></p>
                      )}
                      {testResult.details.user.email && (
                        <p className="text-xs text-success-foreground">Email: <span className="font-medium">{testResult.details.user.email}</span></p>
                      )}
                      {testResult.details.user.display_name && (
                        <p className="text-xs text-success-foreground">Display Name: <span className="font-medium">{testResult.details.user.display_name}</span></p>
                      )}
                    </div>
                  )}
                  {testResult.details.rate_limit && (
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-success-foreground">API Rate Limit:</p>
                      <p className="text-xs text-success-foreground">Remaining: <span className="font-medium">{testResult.details.rate_limit.remaining}/{testResult.details.rate_limit.limit}</span></p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* OAuth connect — the fast path. Sign in at the provider, come
              back connected; no tokens to copy. */}
          {offersOAuth && (
            <div className="space-y-3">
              <button
                onClick={handleOAuthConnect}
                disabled={isRedirecting || isSaving || isTesting}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-white bg-success rounded-lg hover:bg-success/90 disabled:bg-disabled disabled:text-disabled-foreground disabled:cursor-not-allowed transition"
              >
                {isRedirecting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ExternalLink className="w-4 h-4" />
                )}
                {isRedirecting ? `Opening ${server.name}...` : `Connect with ${server.name}`}
              </button>
              {!showManualForm ? (
                <button
                  onClick={() => setShowManualForm(true)}
                  className="w-full text-center text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition"
                >
                  Connect a self-hosted instance or use an API token instead
                </button>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-strong" />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    self-hosted instance / API token
                  </span>
                  <div className="flex-1 h-px bg-strong" />
                </div>
              )}
            </div>
          )}

          {/* Connection Name — manual path only; OAuth auto-names */}
          {manualVisible && (
          <div className="space-y-1">
            <label className="block text-sm font-medium text-foreground">
              Connection Name <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={connectionName}
              onChange={(e) => setConnectionName(e.target.value)}
              placeholder={`My ${server.name} Connection`}
              className="w-full px-3 py-2 border border-strong bg-card text-foreground placeholder:text-subtle-foreground rounded-lg focus:ring-2 focus:ring-success focus:border-success"
            />
          </div>
          )}

          {/* Config Fields — render in `required` array order first, then any
              remaining optional keys. Postgres jsonb does not preserve object
              key order (it normalises by length, then alphabetically), so we
              can't rely on Object.keys() reflecting the schema author's intent.
              The `required` array IS preserved because JSON arrays are ordered,
              and it conventionally lists fields in the logical setup order
              (e.g. host → client → username → password). */}
          {manualVisible && server.config_schema?.properties && Object.keys(server.config_schema.properties).length > 0 && (() => {
            const properties = server.config_schema.properties as Record<string, any>
            const required: string[] = server.config_schema?.required || []
            const remaining = Object.keys(properties).filter(k => !required.includes(k)).sort()
            const orderedKeys = [...required.filter(k => k in properties), ...remaining]
            return (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">Configuration</h3>
                {orderedKeys.map(key => renderConfigField(key, properties[key]))}
              </div>
            )
          })()}

          {/* Team Sharing */}
          <div className="flex items-center gap-3 p-4 bg-foreground/[0.04] rounded-lg">
            <input
              type="checkbox"
              id="share-with-team"
              checked={isSharedWithTeam}
              onChange={(e) => setIsSharedWithTeam(e.target.checked)}
              className="h-4 w-4 text-success-foreground focus:ring-success border rounded"
            />
            <label htmlFor="share-with-team" className="text-sm text-foreground">
              Share this connection with my team
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-strong">
          <button
            onClick={handleClose}
            disabled={isSaving || isTesting}
            className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-strong rounded-lg hover:bg-foreground/[0.04] disabled:bg-disabled disabled:text-disabled-foreground disabled:cursor-not-allowed transition"
          >
            Cancel
          </button>
          {manualVisible && (
          <>
          <button
            onClick={handleTest}
            disabled={isSaving || isTesting || !connectionName.trim()}
            className="px-4 py-2 text-sm font-medium text-success-foreground bg-success-soft border border-success/30 rounded-lg hover:bg-success-soft disabled:bg-disabled disabled:text-disabled-foreground disabled:border-transparent disabled:cursor-not-allowed transition flex items-center gap-2"
          >
            {isTesting && <Loader2 className="w-4 h-4 animate-spin" />}
            Test Connection
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || isTesting}
            className="px-4 py-2 text-sm font-medium text-white bg-success rounded-lg hover:bg-success/90 disabled:bg-disabled disabled:text-disabled-foreground disabled:cursor-not-allowed transition flex items-center gap-2"
          >
            {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEditMode ? 'Update Connection' : 'Save Connection'}
          </button>
          </>
          )}
        </div>
      </div>
    </div>
  )
}
